import React, { useEffect, useState } from 'react';
import { useLang } from './i18n';
import { HoverTip } from './HoverTip';

export function VerifiedBadgeSvg({ size = 18 }: { size?: number }) {
  const [, , tr] = useLang();
  return (
    <HoverTip className="verified-badge-tip" label={tr('Verified server', 'Verifizierter Server')}>
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className="flackle-verified-svg"
      aria-hidden="true"
    >
      <path
        d="M12 1.8L14.7 3.7L18 3.4L19.3 6.4L22.2 8.1L21.5 11.3L22.8 14.4L20.3 16.6L19.7 19.9L16.4 20.3L14.2 22.8L11.1 21.6L8 22.8L5.8 20.3L2.5 19.9L1.9 16.6L-0.6 14.4L0.7 11.3L0 8.1L2.9 6.4L4.2 3.4L7.5 3.7L12 1.8Z"
        transform="translate(0.9, 0.2) scale(0.93)"
        fill="#ef5252"
      />
      <path
        d="M8.5 12.3L10.9 14.7L15.8 9.5"
        stroke="#ffffff"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
    </HoverTip>
  );
}

export function UserStatSvg({ color = '#22c55e', size = 13 }: { color?: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={{ flexShrink: 0 }}>
      <circle cx="12" cy="8" r="4.2" />
      <path d="M4.5 20.2C4.5 16.2 7.8 13.5 12 13.5C16.2 13.5 19.5 16.2 19.5 20.2C19.5 20.7 19.1 21 18.5 21H5.5C4.9 21 4.5 20.7 4.5 20.2Z" />
    </svg>
  );
}

const SITE_PAGES=new Set(['api','downloads','download','login','register','developer','files','avatars','fonts','tos','terms','agb','nutzungsbedingungen','privacy','datenschutz','impressum','imprint','legal','legal-notice','android','icon','status']);
export function extractInviteCode(text: string): string | null {
  if (!text) return null;
  // Only real invite links: site pages such as malvo.eu/developer/... are not invites.
  // Bare malvo.eu/<name> is a vanity invite only if it is not a site page (malvo.eu/tos, /privacy, /impressum.html …).
  const direct = text.match(/(?:https?:\/\/)?(?:www\.)?malvo\.eu\/(?:invite\/|i\/|\?invite=)?([A-Za-z0-9_-]{3,32})(?![A-Za-z0-9_\/.-])/i);
  if (direct && !SITE_PAGES.has(direct[1].toLowerCase())) {
    return direct[1];
  }
  const rel = text.match(/(?:^|\s)\/(?:invite\/|i\/|\?invite=)([A-Za-z0-9_-]{3,32})/i);
  if (rel) return rel[1];
  return null;
}

export function InviteEmbed({ text, onJoin,joinedCommunityIds }: { text: string; onJoin?: (code: string,serverId?:string) => void;joinedCommunityIds?:string[] }) {
  const [lang, , tr] = useLang();
  const code = extractInviteCode(text);
  const [server, setServer] = useState<{
    id: string;
    name: string;
    members: number;
    online: number;
    color: string;
    icon?: string;
    verified?: number;
    description?: string;
    joined?: boolean;
  } | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setServer(null);
    setFailed(false);
    if (!code) return;
    const c = new AbortController();
    fetch('/api/invites/' + encodeURIComponent(code), { signal: c.signal, cache: 'no-store' })
      .then(async r => {
        if (!r.ok) throw Error();
        return r.json();
      })
      .then(setServer)
      .catch(e => {
        if (e.name !== 'AbortError') setFailed(true);
      });
    return () => c.abort();
  }, [code]);

  if (!code) return null;
  const isOfficial = server?.id === 'malvo-official' || code.toLowerCase() === 'malvo';
  const iconSrc =
    isOfficial || server?.icon === 'icon.png'
      ? '/icon.png'
      : server?.icon
      ? '/api/files/' + server.icon
      : null;
  const vanityUrl = isOfficial ? 'malvo.eu/malvo' : `malvo.eu/i/${code}`;

  return (
    <a
      className="flackle-invite-card"
      href={isOfficial ? '/malvo' : '/?invite=' + encodeURIComponent(code)}
      onClick={e => {
        if (onJoin && !failed) {
          e.preventDefault();
          onJoin(code,server?.id);
        }
      }}
    >
      <div className="invite-card-header">
        <div className="invite-card-kicker">
          {isOfficial
            ? tr('OFFICIAL MALVO SERVER', 'OFFIZIELLER MALVO SERVER')
            : tr('SERVER INVITE', 'SERVER-EINLADUNG')}
        </div>
        <div className="invite-card-vanity">{vanityUrl}</div>
      </div>
      <div className="invite-card-main">
        <div className="invite-card-logo">
          {iconSrc ? (
            <img src={iconSrc} alt={server?.name || 'Server'} />
          ) : (
            server?.name.slice(0, 1).toUpperCase() || 'M'
          )}
        </div>
        <div className="invite-card-info">
          <div className="invite-card-title">
            <b>
              {server?.name ||
                (failed ? tr('Invalid Invite', 'Einladung ungültig') : tr('Loading Server…', 'Lade Server …'))}
            </b>
            {(server?.verified || isOfficial) && <VerifiedBadgeSvg size={17} />}
          </div>
          {server?.description && <div className="invite-card-desc">{server.description}</div>}
          <div className="invite-card-stats">
            {server ? (
              <>
                <div className="stat-online">
                  <UserStatSvg color="#22c55e" size={14} />
                  <span>{server.online}</span>
                </div>
                <div className="stat-total">
                  <UserStatSvg color="#8e8e9a" size={14} />
                  <span>{server.members.toLocaleString(lang === 'de' ? 'de-DE' : 'en-US')}</span>
                </div>
              </>
            ) : (
              <div>MALVO Community</div>
            )}
          </div>
        </div>
        {!failed && (
          <div className={'invite-card-btn ' + ((server&&(joinedCommunityIds?joinedCommunityIds.includes(server.id):server.joined)) ? 'is-joined' : '')}>
            {server&&(joinedCommunityIds?joinedCommunityIds.includes(server.id):server.joined) ? tr('Open server', 'Server öffnen') : tr('Join', 'Beitreten')}
          </div>
        )}
      </div>
    </a>
  );
}
